import { memo, useMemo, useState } from 'react';

import {
  type ColumnFiltersState,
  type RowSelectionState,
  type Table,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { type ColumnMappingState, FIELD_TYPE_OPTIONS, MULTI_VALUE_OPTIONS } from '../helpers';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { includedInArr, selectColumn, selectedRowsFilter } from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';

interface MappingStepProps {
  columnMapping: ColumnMappingState[];
  sampleValues: string[];
  multiValueDelimiter: string;
  onChange: (columnMapping: ColumnMappingState[]) => void;
}

interface MappingRow {
  index: number;
  col: ColumnMappingState;
  sample: string;
}

interface MappingTableMeta {
  updateAt: (index: number, partial: Partial<ColumnMappingState>) => void;
}

const getMeta = (table: Table<MappingRow>) => table.options.meta as MappingTableMeta;

/**
 * The interactive cells are memoised on primitive props (+ the stable table instance),
 * so toggling one row's checkbox only re-renders that row — not every row's Radix
 * Select/Switch. This is the difference between a snappy and a sluggish table at scale.
 */
interface CellProps {
  index: number;
  disabled: boolean;
  table: Table<MappingRow>;
}

const FieldNameCell = memo(function FieldNameCell({
  index,
  disabled,
  table,
  displayName,
  originalName,
}: CellProps & { displayName: string; originalName: string }) {
  return (
    <div className="flex max-w-[280px] items-center gap-1">
      <span className={cn('min-w-0 truncate', disabled ? 'text-gray-400' : 'text-gray-800')}>{displayName}</span>
      {!disabled && (
        <>
          <EditFieldDialog
            title="Edit field name"
            description="Update the name this column maps to in Wexler."
            fieldLabel="Field name"
            currentValue={displayName}
            onSave={(value) => getMeta(table).updateAt(index, { displayName: value })}
          />
          {displayName !== originalName && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="px-1 py-1 text-gray-400 hover:text-gray-800"
                  onClick={() => getMeta(table).updateAt(index, { displayName: originalName })}
                >
                  <Icon className="lucide--rotate-ccw" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset to original name</TooltipContent>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
});

const SampleCell = memo(function SampleCell({ value }: { value: string }) {
  if (!value) return <span className="text-gray-300">—</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block max-w-[300px] truncate text-gray-600">{value}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm break-all" align="start">
        {value}
      </TooltipContent>
    </Tooltip>
  );
});

const TypeCell = memo(function TypeCell({ index, disabled, table, fieldType }: CellProps & { fieldType: string }) {
  return (
    <Select
      value={fieldType}
      onValueChange={(v) => getMeta(table).updateAt(index, { fieldType: v })}
      disabled={disabled}
    >
      <SelectTrigger className="h-auto w-40 rounded-md border-none px-1.5 py-0 shadow-none ring-offset-0 hover:bg-gray-100 focus-visible:ring-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {FIELD_TYPE_OPTIONS.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});

const ArrayCell = memo(function ArrayCell({ index, disabled, table, isArray }: CellProps & { isArray: boolean }) {
  return (
    <Switch
      checked={isArray}
      onCheckedChange={(v) => getMeta(table).updateAt(index, { isArray: v })}
      disabled={disabled}
    />
  );
});

const columnHelper = createColumnHelper<MappingRow>();

export function MappingStep({ columnMapping, sampleValues, multiValueDelimiter, onChange }: MappingStepProps) {
  const multiValueDelimiterLabel =
    MULTI_VALUE_OPTIONS.find((o) => o.value === multiValueDelimiter)?.label ?? multiValueDelimiter;

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const rows = useMemo<MappingRow[]>(
    () => columnMapping.map((col, index) => ({ index, col, sample: sampleValues[index] ?? '' })),
    [columnMapping, sampleValues],
  );

  // Row selection is derived from `enabled` and written straight back through onChange,
  // so the parent's columnMapping stays the single source of truth.
  const rowSelection = useMemo<RowSelectionState>(
    () => Object.fromEntries(columnMapping.map((col, index) => [String(index), col.enabled])),
    [columnMapping],
  );

  const columns = useMemo(
    () => [
      { ...selectColumn<MappingRow>(), filterFn: selectedRowsFilter },
      columnHelper.accessor((row) => row.col.header, {
        id: 'field',
        header: () => <span>Field name</span>,
        cell: (info) => (
          <FieldNameCell
            index={info.row.original.index}
            displayName={info.row.original.col.displayName}
            originalName={info.row.original.col.header}
            disabled={!info.row.getIsSelected()}
            table={info.table}
          />
        ),
      }),
      columnHelper.accessor((row) => row.sample, {
        id: 'sample',
        enableSorting: false,
        header: () => (
          <Tooltip>
            <TooltipTrigger>
              <span className="flex items-center gap-2">
                Sample value
                <Icon className="lucide--info" />
              </span>
            </TooltipTrigger>
            <TooltipContent>The first non-empty value found in the 50 sample rows.</TooltipContent>
          </Tooltip>
        ),
        cell: (info) => <SampleCell value={info.getValue()} />,
      }),
      columnHelper.accessor((row) => row.col.fieldType, {
        id: 'fieldType',
        header: () => (
          <Tooltip>
            <TooltipTrigger>
              <span className="flex items-center gap-2">
                Type
                <Icon className="lucide--alert-circle text-amber-500" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              These types have been inferred based on the shape of the sample data. Please verify each selected field.
            </TooltipContent>
          </Tooltip>
        ),
        filterFn: includedInArr,
        cell: (info) => (
          <TypeCell
            index={info.row.original.index}
            fieldType={info.row.original.col.fieldType}
            disabled={!info.row.getIsSelected()}
            table={info.table}
          />
        ),
      }),
      columnHelper.display({
        id: 'array',
        header: () => (
          <Tooltip>
            <TooltipTrigger>
              <span className="flex items-center justify-center gap-2">
                Array
                <Icon className="lucide--info" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Based on the selected delimiter <strong>{multiValueDelimiterLabel}</strong>
            </TooltipContent>
          </Tooltip>
        ),
        cell: (info) => (
          <ArrayCell
            index={info.row.original.index}
            isArray={info.row.original.col.isArray ?? false}
            disabled={!info.row.getIsSelected()}
            table={info.table}
          />
        ),
      }),
    ],
    [multiValueDelimiterLabel],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => String(row.index),
    enableRowSelection: true,
    state: { globalFilter, columnFilters, rowSelection },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      onChange(columnMapping.map((col, index) => ({ ...col, enabled: !!next[String(index)] })));
    },
    meta: {
      updateAt: (index: number, partial: Partial<ColumnMappingState>) =>
        onChange(columnMapping.map((col, i) => (i === index ? { ...col, ...partial } : col))),
    } satisfies MappingTableMeta,
  });

  const showOnlySelected = Boolean(table.getColumn('select')?.getFilterValue());

  const showOnlySelectedToggle = (
    <label className="flex shrink-0 items-center gap-2 px-2">
      <Checkbox
        checked={showOnlySelected}
        onCheckedChange={(checked) => table.getColumn('select')?.setFilterValue(checked ? true : undefined)}
      />
      <span className="text-sm font-medium text-gray-700">Only show selected fields</span>
    </label>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        additionalButtons={showOnlySelectedToggle}
        plural="fields"
        className="min-h-0 flex-1 px-0"
        hidePagination
      />
    </div>
  );
}
